import type { ClassroomConnectRequest } from '@study-decks/shared'

import { challengeFromVerifier, generateState, generateVerifier } from './pkce.js'

const VERIFIER_KEY = 'sd.classroom.verifier'
const STATE_KEY = 'sd.classroom.state'
const RETURN_TO_KEY = 'sd.classroom.return_to'
const CALLBACK_PATH = '/auth/callback'
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth'
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''

export class ClassroomConnectError extends Error {
  override readonly name = 'ClassroomConnectError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

export interface ClassroomAuthUrlOptions {
  challenge: string
  clientId: string
  domainHint?: string
  redirectUri: string
  state: string
}

export function classroomRedirectUri(origin = window.location.origin): string {
  return `${origin}${CALLBACK_PATH}`
}

export function isClassroomConnectCallback(
  href = window.location.href,
  storage: Pick<Storage, 'getItem'> = sessionStorage,
): boolean {
  const state = new URL(href).searchParams.get('state')
  return !!state && storage.getItem(STATE_KEY) === state && !!storage.getItem(VERIFIER_KEY)
}

export function buildClassroomAuthUrl({
  challenge,
  clientId,
  domainHint,
  redirectUri,
  state,
}: ClassroomAuthUrlOptions): string {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: [
      'openid',
      'email',
      'profile',
      'https://www.googleapis.com/auth/classroom.courses.readonly',
      'https://www.googleapis.com/auth/classroom.rosters.readonly',
      'https://www.googleapis.com/auth/classroom.profile.emails',
    ].join(' '),
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    access_type: 'offline',
    prompt: 'consent select_account',
  })
  if (domainHint) params.set('hd', domainHint)
  return `${GOOGLE_AUTH_URL}?${params.toString()}`
}

export async function startClassroomConnect(returnTo = '/teacher/classes'): Promise<void> {
  const verifier = generateVerifier()
  const challenge = await challengeFromVerifier(verifier)
  const state = generateState()

  sessionStorage.setItem(VERIFIER_KEY, verifier)
  sessionStorage.setItem(STATE_KEY, state)
  sessionStorage.setItem(RETURN_TO_KEY, returnTo)

  const authUrl = buildClassroomAuthUrl({
    challenge,
    clientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
    domainHint: import.meta.env.VITE_DOMAIN_HINT,
    redirectUri: classroomRedirectUri(),
    state,
  })
  window.location.assign(authUrl)
}

function friendlyMessage(code: string, fallback: string): string {
  if (code === 'MISSING_REFRESH_TOKEN') {
    return "Google didn't return a refresh token. Try reconnecting."
  }
  if (code === 'EMAIL_MISMATCH') {
    return "The Google account you connected doesn't match your sign-in. Sign out, sign in with the right account, then reconnect."
  }
  return fallback
}

export async function completeClassroomConnect(idToken: string): Promise<string> {
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  const error = url.searchParams.get('error')

  if (error) throw new ClassroomConnectError('GOOGLE_ERROR', `Google consent error: ${error}`)
  if (!code) throw new ClassroomConnectError('BAD_CALLBACK', 'Missing auth code')
  if (!state) throw new ClassroomConnectError('BAD_CALLBACK', 'Missing state')

  const expectedState = sessionStorage.getItem(STATE_KEY)
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? '/teacher/classes'

  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(RETURN_TO_KEY)

  if (!expectedState || state !== expectedState) {
    throw new ClassroomConnectError('STATE_MISMATCH', 'State mismatch — please reconnect.')
  }
  if (!verifier) {
    throw new ClassroomConnectError('MISSING_VERIFIER', 'Missing PKCE verifier — please reconnect.')
  }

  const body: ClassroomConnectRequest = {
    code,
    codeVerifier: verifier,
    redirectUri: classroomRedirectUri(),
  }
  const res = await fetch(`${API_BASE_URL}/api/classroom/connect`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${idToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const errorBody = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    const errorCode = errorBody.error?.code ?? 'CONNECT_FAILED'
    throw new ClassroomConnectError(
      errorCode,
      friendlyMessage(errorCode, errorBody.error?.message ?? `Connect failed (${res.status})`),
    )
  }
  return returnTo
}
